<template>
  <div class="module-pricing">
    <ContentWrap>
      <el-alert
        title="可配置是否需要经营模式：双模式须分别配平台/自营价；正常模块只配标准试用与月年价，购买时不选模式（biz_mode=NULL）。OSS 容量包不在此页。"
        type="info"
        :closable="false"
        class="mb-15px"
      />
    </ContentWrap>

    <ContentWrap>
      <el-table v-loading="loading" :data="tableData" stripe>
        <el-table-column label="模块标识" prop="moduleCode" min-width="110" />
        <el-table-column label="模块名称" min-width="140">
          <template #default="{ row }">
            <span>{{ row.moduleName || row.moduleCode }}</span>
          </template>
        </el-table-column>
        <el-table-column label="定价方案" prop="planName" width="100" />
        <el-table-column label="经营模式" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.bizModeRequired === 0 ? 'info' : 'warning'" size="small">
              {{ row.bizModeRequired === 0 ? '正常' : '双模式' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="平台试用" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="row.trialDaysPlatform > 0 ? 'success' : 'info'" size="small">
              {{ row.trialDaysPlatform > 0 ? row.trialDaysPlatform + '天' : '不支持' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="自营试用" width="90" align="center">
          <template #default="{ row }">
            <template v-if="row.bizModeRequired === 0">
              <span class="text-gray-400">—</span>
            </template>
            <el-tag v-else :type="row.trialDaysSelf > 0 ? 'success' : 'info'" size="small">
              {{ row.trialDaysSelf > 0 ? row.trialDaysSelf + '天' : '不支持' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="平台月/年" min-width="150" align="right">
          <template #default="{ row }">
            <div>{{ formatPrice(row.priceMonthlyPlatform) }} / {{ formatPrice(row.priceYearlyPlatform) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="自营月/年" min-width="150" align="right">
          <template #default="{ row }">
            <template v-if="row.bizModeRequired === 0">
              <span class="text-gray-400">—</span>
            </template>
            <div v-else>{{ formatPrice(row.priceMonthlySelf) }} / {{ formatPrice(row.priceYearlySelf) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right" align="center">
          <template #default="{ row }">
            <el-button
              v-hasPermi="['saasbilling:pricing:update']"
              link
              type="primary"
              @click="openDialog(row)"
            >
              编辑定价
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </ContentWrap>

    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="560px"
      :close-on-click-modal="false"
    >
      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="120px">
        <el-form-item label="模块标识">
          <el-input :model-value="formData.moduleCode" disabled />
        </el-form-item>
        <el-form-item label="方案名称" prop="planName">
          <el-input v-model="formData.planName" placeholder="如：标准版、专业版" maxlength="64" />
        </el-form-item>
        <el-form-item label="经营模式" prop="bizModeRequired">
          <el-radio-group v-model="formData.bizModeRequired">
            <el-radio :value="1">双模式（须选平台/自营）</el-radio>
            <el-radio :value="0">正常（不选模式，biz_mode=NULL）</el-radio>
          </el-radio-group>
        </el-form-item>

        <template v-if="formData.bizModeRequired === 0">
          <el-divider content-position="left">标准定价（正常模块）</el-divider>
          <el-form-item label="试用天数" prop="trialDaysPlatform">
            <el-input-number
              v-model="formData.trialDaysPlatform"
              :min="0"
              :max="365"
              controls-position="right"
              style="width: 200px"
            />
            <span class="text-gray-400 ml-8px">0 = 不支持试用</span>
          </el-form-item>
          <el-form-item label="月付" prop="priceMonthlyPlatform">
            <el-input-number
              v-model="formData.priceMonthlyPlatform"
              :min="0"
              :step="100"
              controls-position="right"
              style="width: 200px"
            />
            <span class="text-gray-400 ml-8px"
              >{{ yuan(formData.priceMonthlyPlatform) }} 元 | 0 = 不支持</span
            >
          </el-form-item>
          <el-form-item label="年付" prop="priceYearlyPlatform">
            <el-input-number
              v-model="formData.priceYearlyPlatform"
              :min="0"
              :step="100"
              controls-position="right"
              style="width: 200px"
            />
            <span class="text-gray-400 ml-8px"
              >{{ yuan(formData.priceYearlyPlatform) }} 元 | 0 = 不支持</span
            >
          </el-form-item>
        </template>

        <template v-else>
        <el-divider content-position="left">平台模式</el-divider>
        <el-form-item label="平台试用天" prop="trialDaysPlatform">
          <el-input-number
            v-model="formData.trialDaysPlatform"
            :min="0"
            :max="365"
            controls-position="right"
            style="width: 200px"
          />
          <span class="text-gray-400 ml-8px">0 = 平台不支持试用</span>
        </el-form-item>
        <el-form-item label="平台月付" prop="priceMonthlyPlatform">
          <el-input-number
            v-model="formData.priceMonthlyPlatform"
            :min="0"
            :step="100"
            controls-position="right"
            style="width: 200px"
          />
          <span class="text-gray-400 ml-8px"
            >{{ yuan(formData.priceMonthlyPlatform) }} 元 | 0 = 不支持</span
          >
        </el-form-item>
        <el-form-item label="平台年付" prop="priceYearlyPlatform">
          <el-input-number
            v-model="formData.priceYearlyPlatform"
            :min="0"
            :step="100"
            controls-position="right"
            style="width: 200px"
          />
          <span class="text-gray-400 ml-8px"
            >{{ yuan(formData.priceYearlyPlatform) }} 元 | 0 = 不支持</span
          >
        </el-form-item>

        <el-divider content-position="left">自营模式</el-divider>
        <el-form-item label="自营试用天" prop="trialDaysSelf">
          <el-input-number
            v-model="formData.trialDaysSelf"
            :min="0"
            :max="365"
            controls-position="right"
            style="width: 200px"
          />
          <span class="text-gray-400 ml-8px">0 = 自营不支持试用</span>
        </el-form-item>
        <el-form-item label="自营月付" prop="priceMonthlySelf">
          <el-input-number
            v-model="formData.priceMonthlySelf"
            :min="0"
            :step="100"
            controls-position="right"
            style="width: 200px"
          />
          <span class="text-gray-400 ml-8px"
            >{{ yuan(formData.priceMonthlySelf) }} 元 | 0 = 不支持</span
          >
        </el-form-item>
        <el-form-item label="自营年付" prop="priceYearlySelf">
          <el-input-number
            v-model="formData.priceYearlySelf"
            :min="0"
            :step="100"
            controls-position="right"
            style="width: 200px"
          />
          <span class="text-gray-400 ml-8px"
            >{{ yuan(formData.priceYearlySelf) }} 元 | 0 = 不支持</span
          >
        </el-form-item>
        </template>

        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="formData.status">
            <el-radio :value="1">启用</el-radio>
            <el-radio :value="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button :disabled="submitting" type="primary" @click="handleSubmit">确 定</el-button>
        <el-button @click="dialogVisible = false">取 消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import * as ModuleApi from '@/api/system/module/index'
import * as TenantModuleApi from '@/api/system/tenantModule'
import { ElMessage } from 'element-plus'

defineOptions({ name: 'ModulePricing' })

interface PricingRow extends TenantModuleApi.PricingVO {
  moduleName?: string
}

const loading = ref(true)
const tableData = ref<PricingRow[]>([])
const dialogVisible = ref(false)
const dialogTitle = ref('编辑定价')
const submitting = ref(false)

const emptyForm = (): TenantModuleApi.PricingVO => ({
  id: undefined,
  moduleCode: '',
  planName: '标准版',
  trialDaysPlatform: 0,
  trialDaysSelf: 0,
  priceMonthlyPlatform: 0,
  priceYearlyPlatform: 0,
  priceMonthlySelf: 0,
  priceYearlySelf: 0,
  bizModeRequired: 1,
  status: 1
})

const formData = ref<TenantModuleApi.PricingVO>(emptyForm())

const formRules = {
  planName: [{ required: true, message: '方案名称不能为空', trigger: 'blur' }],
  bizModeRequired: [{ required: true, message: '请选择是否需要经营模式', trigger: 'change' }],
  trialDaysPlatform: [{ required: true, message: '试用天数不能为空', trigger: 'blur' }],
  trialDaysSelf: [{ required: true, message: '自营试用天数不能为空', trigger: 'blur' }],
  priceMonthlyPlatform: [{ required: true, message: '月付不能为空', trigger: 'blur' }],
  priceYearlyPlatform: [{ required: true, message: '年付不能为空', trigger: 'blur' }],
  priceMonthlySelf: [{ required: true, message: '自营月付不能为空', trigger: 'blur' }],
  priceYearlySelf: [{ required: true, message: '自营年付不能为空', trigger: 'blur' }],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
}

const formRef = ref()

const yuan = (fen?: number) => ((fen || 0) / 100).toFixed(2)
const formatPrice = (fen?: number) => {
  if (!fen || fen <= 0) return '不支持'
  return `${(fen / 100).toFixed(2)}元`
}

const normalizePricing = (p?: TenantModuleApi.PricingVO | null): TenantModuleApi.PricingVO => {
  const monthly = p?.priceMonthlyPlatform ?? p?.priceMonthly ?? 0
  const yearly = p?.priceYearlyPlatform ?? p?.priceYearly ?? 0
  const trialPlatform = p?.trialDaysPlatform ?? p?.trialDays ?? 0
  const trialSelf = p?.trialDaysSelf ?? p?.trialDays ?? trialPlatform
  const bizModeRequired = p?.bizModeRequired === 0 ? 0 : 1
  return {
    id: p?.id,
    moduleCode: p?.moduleCode || '',
    planName: p?.planName || '标准版',
    trialDaysPlatform: trialPlatform,
    trialDaysSelf: trialSelf,
    priceMonthlyPlatform: monthly,
    priceYearlyPlatform: yearly,
    priceMonthlySelf: p?.priceMonthlySelf ?? monthly,
    priceYearlySelf: p?.priceYearlySelf ?? yearly,
    bizModeRequired,
    status: p?.status ?? 1
  }
}

const loadData = async () => {
  loading.value = true
  try {
    const moduleRes = await ModuleApi.getModulePage({
      pageNo: 1,
      pageSize: 200,
      type: undefined,
      status: undefined
    })
    const modules = moduleRes.list || []
    const pricingList = await TenantModuleApi.getPricingList()
    const pricingMap: Record<string, TenantModuleApi.PricingVO> = {}
    for (const p of pricingList) {
      pricingMap[p.moduleCode] = normalizePricing(p)
    }

    const rows: PricingRow[] = []
    for (const m of modules) {
      const pricing = pricingMap[m.name]
      rows.push({
        ...normalizePricing(
          pricing || {
            moduleCode: m.name,
            planName: '标准版',
            trialDays: 0,
            priceMonthlyPlatform: 0,
            priceYearlyPlatform: 0,
            priceMonthlySelf: 0,
            priceYearlySelf: 0,
            trialDaysPlatform: 0,
            trialDaysSelf: 0,
            status: 1
          }
        ),
        moduleCode: m.name,
        moduleName: m.title
      })
    }

    for (const p of pricingList) {
      if (!rows.some((r) => r.moduleCode === p.moduleCode)) {
        rows.push({
          ...normalizePricing(p),
          moduleName: p.moduleCode
        })
      }
    }

    tableData.value = rows
  } finally {
    loading.value = false
  }
}

const openDialog = (row: PricingRow) => {
  dialogTitle.value = `编辑定价 - ${row.moduleName || row.moduleCode}`
  formData.value = {
    ...normalizePricing(row),
    moduleCode: row.moduleCode
  }
  dialogVisible.value = true
}

const handleSubmit = async () => {
  const valid = await formRef.value?.validate()
  if (!valid) return
  submitting.value = true
  try {
    await TenantModuleApi.savePricing(formData.value)
    ElMessage.success('定价保存成功')
    dialogVisible.value = false
    await loadData()
  } catch {
    // error handled by interceptor
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  loadData()
})
</script>
